// Сравнение периодов в отчётах.
//
// Модель: сравнение — это ПАТЧ поверх базового payload отчёта, а не «второй набор дат».
// Патч по датам (предыдущий период / год назад / произвольные даты) — частный случай;
// `overrides` позволяет тем же кодом сравнивать «бот А vs бот Б», «с фильтром vs без»,
// а в перспективе — сегменты/когорты (кладём их в overrides.source_request).
//
// Конфиг живёт в `visual.compare`: поле `visual` бэкендом не читается вообще
// (BuilderController::actionUseReportTemplate парсит только source_request + settings),
// поэтому сравнение сохраняется вместе с отчётом и виджетом дашборда без миграций.
//
// Ось X у трендов бэкенд строит синтетически из :date_from/:date_to
// (TrendsQueryService::getSelectDate — arrayMap по range(0, dateDiff+1)), поэтому
// массивы date[]/total[] всегда плотные и покрывают весь запрошенный интервал.
// Период той же длины и гранулярности даёт столько же точек → выравнивание по индексу.
// На равенство длин всё же не полагаемся (год назад, високосные годы, месяцы) —
// отрисовка обрезает/добивает null'ами.

import { isUtmTrendsBreakdown } from './utmTrends'

export const COMPARE_MODES = ['previous', 'previous_year', 'custom']

// Типы визуализации, где сравнение отрисовывается. Остальные (воронки, ретеншен,
// пути, utm, списки) — отдельная итерация со своим дизайном (колонка дельты вместо
// второй линии), поэтому запрос сравнения для них не делается вовсе.
export const COMPARE_CHART_TYPES = ['line', 'area', 'line_cumulative', 'bar', 'bar_stacked', 'number']

export function isCompareSupported(type, typeChart) {
    // utm_trends — линии по UTM-меткам: и у трендов с включённой разбивкой по UTM
    // (там type_chart подменяется на utm_trends), и у самостоятельного UTM-отчёта
    if (typeChart === 'utm_trends') return type === 'trends' || type === 'utm'
    if (type === 'trends') return COMPARE_CHART_TYPES.includes(typeChart)
    // SQL-режим: :date_from/:date_to подставляются параметрами в сам запрос, поэтому
    // сравнение работает тем же сдвигом. Плюс собственный вид «сырые строки» (table).
    if (type === 'sql') return typeChart === 'table' || COMPARE_CHART_TYPES.includes(typeChart)
    return false
}

/**
 * Ось основного ответа для расчёта сравнительного периода — метки bucket'ов в поясе
 * бэкенда. У трендов это готовый плотный массив `date`, у utm-разбивки трендов ряды
 * плоские (строка на UTM×день), поэтому ось собирается из уникальных `day_start`; она
 * разрежена (дни без событий отсутствуют), но её края — настоящие даты, а этого для
 * сдвига хватает. Принимает весь reportData (а не только type/type_chart), потому что
 * "это utm-режим" теперь зависит ещё и от source_request.trends_utm_breakdown — см.
 * helpers/reports/utmTrends.js.
 */
export function extractCompareAxis(reportData, chartData) {
    if (!Array.isArray(chartData) || !chartData.length) return null
    if (isUtmTrendsBreakdown(reportData)) {
        const days = new Set()
        for (const row of chartData) {
            if (row?.day_start) days.add(row.day_start)
        }
        return days.size ? { labels: Array.from(days).sort(), sparse: true } : null
    }
    if (reportData.type === 'trends') {
        const labels = chartData[0]?.date
        return Array.isArray(labels) && labels.length ? { labels, sparse: false } : null
    }
    return null
}

/**
 * Приводит visual.compare к предсказуемой форме. Возвращает null, если сравнение выключено.
 * @param {object|null} visual
 * @returns {{mode: string, date_from: ?string, date_to: ?string, overrides: ?object}|null}
 */
export function normalizeCompare(visual) {
    const c = visual?.compare
    if (!c || typeof c !== 'object' || !c.enabled) return null
    return {
        mode: COMPARE_MODES.includes(c.mode) ? c.mode : 'previous',
        date_from: c.date_from || null,
        date_to: c.date_to || null,
        overrides: (c.overrides && typeof c.overrides === 'object' && !Array.isArray(c.overrides))
            ? c.overrides
            : null
    }
}

/**
 * Разбирает значение :date_from/:date_to в Date по тем же правилам, что и бэкенд
 * (TypeDefinitionHelper::isDate): `-Nd` = сейчас минус N суток (время текущее),
 * `now` = сейчас, `today` = начало суток, плюс литералы Y-m-d[ H:i:s].
 * @returns {Date|null}
 */
export function resolveDateToken(token) {
    if (token instanceof Date) return new Date(token.getTime())
    const s = String(token ?? '').trim()
    if (!s) return null

    const rel = /^-(\d+)d$/.exec(s)
    if (rel) {
        const d = new Date()
        d.setDate(d.getDate() - parseInt(rel[1], 10))
        return d
    }
    if (s.toLowerCase() === 'now') return new Date()
    if (s.toLowerCase() === 'today') {
        const d = new Date()
        d.setHours(0, 0, 0, 0)
        return d
    }

    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s)
    if (m) {
        return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0))
    }
    return null
}

/** 'YYYY-MM-DD HH:mm:ss' — формат, который принимает бэкенд как литеральную дату. */
export function formatDateParam(date) {
    const p = (n) => String(n).padStart(2, '0')
    return date.getFullYear() + '-' + p(date.getMonth() + 1) + '-' + p(date.getDate()) +
        ' ' + p(date.getHours()) + ':' + p(date.getMinutes()) + ':' + p(date.getSeconds())
}

/** Начало bucket'а — зеркало toStartOf* из TrendsQueryService::getSelectDate. */
export function startOfBucket(date, granularity) {
    const d = new Date(date.getTime())
    switch (granularity) {
        case 'minute':
            // бэкенд группирует по toStartOfTenMinutes
            d.setMinutes(Math.floor(d.getMinutes() / 10) * 10, 0, 0)
            break
        case 'hour':
            d.setMinutes(0, 0, 0)
            break
        case 'week':
            d.setHours(0, 0, 0, 0)
            // ClickHouse toStartOfWeek по умолчанию считает неделю с воскресенья
            d.setDate(d.getDate() - d.getDay())
            break
        case 'month':
            d.setHours(0, 0, 0, 0)
            d.setDate(1)
            break
        default:
            d.setHours(0, 0, 0, 0)
    }
    return d
}

/**
 * Сколько точек вернёт бэкенд для этого окна. Нужно, чтобы сдвинуть период ровно
 * на его собственную длину и получить непрерывный предыдущий отрезок.
 */
export function bucketCount(fromDate, toDate, granularity) {
    const a = startOfBucket(fromDate, granularity)
    const b = startOfBucket(toDate, granularity)
    const ms = b.getTime() - a.getTime()
    switch (granularity) {
        case 'minute':
            return Math.max(1, Math.round(ms / 600000) + 1)
        case 'hour':
            return Math.max(1, Math.round(ms / 3600000) + 1)
        case 'week':
            return Math.max(1, Math.round(ms / (7 * 86400000)) + 1)
        case 'month':
            return Math.max(1, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1)
        default:
            // round, а не floor: переход на летнее время делает сутки 23/25 часов
            return Math.max(1, Math.round(ms / 86400000) + 1)
    }
}

/** Сдвиг на n bucket'ов выбранной гранулярности. Отрицательный n — вперёд. */
export function shiftBack(date, granularity, n) {
    const d = new Date(date.getTime())
    switch (granularity) {
        case 'minute': d.setMinutes(d.getMinutes() - n * 10); break
        case 'hour': d.setHours(d.getHours() - n); break
        case 'week': d.setDate(d.getDate() - n * 7); break
        case 'month': d.setMonth(d.getMonth() - n); break
        case 'year': d.setFullYear(d.getFullYear() - n); break
        default: d.setDate(d.getDate() - n)
    }
    return d
}

/**
 * Переносит метку дня сравнительного периода вперёд на тот же сдвиг, которым период
 * и строился, — получается соответствующая ей метка ОСНОВНОГО периода.
 *
 * Нужно там, где ось не плотная и выравнивать по индексу нельзя (utm_trends: строка
 * на UTM×день, дни без событий просто отсутствуют, и у двух периодов пропуски разные).
 *
 * @param {string} key   метка вида 'YYYY-MM-DD HH:mm:ss'
 * @param {{amount: number, unit: string}} shift
 * @returns {string|null}
 */
export function shiftDateKeyForward(key, shift) {
    return shiftDateKey(key, shift, -1)
}

/** Обратное к shiftDateKeyForward: метка основного периода → метка сравнительного. */
export function shiftDateKeyBack(key, shift) {
    return shiftDateKey(key, shift, 1)
}

function shiftDateKey(key, shift, sign) {
    if (!shift || !shift.amount) return null
    const d = resolveDateToken(key)
    if (!d) return null
    return formatDateParam(shiftBack(d, shift.unit, sign * shift.amount))
}

/**
 * Период сравнения в виде литеральных дат (относительные токены здесь уже не нужны:
 * период посчитан от текущего окна и не должен «плыть» между двумя запросами).
 *
 * `axis` — фактические метки bucket'ов из ответа бэкенда (`chartData[0].date`). Если
 * она есть, считаем ОТ НЕЁ, а не от `-30d`/`now`. Это принципиально: относительные
 * токены резолвит бэкенд в СВОЁМ часовом поясе, и у клиента в другом поясе граница
 * суток съезжает — `-30d` в 00:08 UTC+1 даёт 2 июля, а бэкенд в UTC берёт 1 июля,
 * и сравнительный период накладывается на текущий. Метки оси приходят готовыми
 * литералами в поясе бэкенда, сдвиг на целые сутки/недели/месяцы их сохраняет.
 *
 * @param {{labels: string[], sparse: boolean}|string[]} [axis] ось основного ответа (extractCompareAxis)
 * @returns {{from: string, to: string, fromDate: Date, toDate: Date}|null}
 */
export function resolveComparePeriod(dateFrom, dateTo, compare, granularity = 'day', axis = null) {
    if (!compare) return null

    if (compare.mode === 'custom') {
        const f = resolveDateToken(compare.date_from)
        const t = resolveDateToken(compare.date_to)
        if (!f || !t) return null
        // Произвольный период не связан с основным сдвигом, переносить метки не по чему —
        // выравнивание там остаётся по порядковому номеру точки
        return { from: formatDateParam(f), to: formatDateParam(t), fromDate: f, toDate: t, shift: null }
    }

    // Ось ответа — источник правды. Фолбэк на резолв токенов нужен для предпросмотра
    // периода в UI до первого ответа и для отчётов без датной оси (SQL).
    const labels = Array.isArray(axis) ? axis : (axis?.labels || null)
    const useAxis = Array.isArray(labels) && labels.length > 0
    const sparse = !Array.isArray(axis) && !!axis?.sparse

    let baseFrom, baseTo
    if (useAxis && sparse) {
        // Разрежённая ось (utm_trends: строка на UTM×день, дни без событий отсутствуют).
        // Её КРАЯ брать нельзя: если в первые дни периода не было ни одного события,
        // окно сравнения уехало бы вперёд и наложилось на текущий период. Поэтому длину
        // берём из запрошенного окна — это длительность, она от часового пояса не зависит,
        // — а якорем берём последний день с данными (реальный литерал в поясе бэкенда).
        const tokenFrom = resolveDateToken(dateFrom)
        const tokenTo = resolveDateToken(dateTo)
        const n = (tokenFrom && tokenTo) ? bucketCount(tokenFrom, tokenTo, granularity) : labels.length
        baseTo = resolveDateToken(labels[labels.length - 1])
        baseFrom = baseTo ? shiftBack(baseTo, granularity, n - 1) : null
    } else if (useAxis) {
        baseFrom = resolveDateToken(labels[0])
        baseTo = resolveDateToken(labels[labels.length - 1])
    } else {
        baseFrom = resolveDateToken(dateFrom)
        baseTo = resolveDateToken(dateTo)
    }
    if (!baseFrom || !baseTo) return null

    let f, t, shift
    if (compare.mode === 'previous_year') {
        f = shiftBack(baseFrom, 'year', 1)
        t = shiftBack(baseTo, 'year', 1)
        shift = { amount: 1, unit: 'year' }
    } else {
        // Сдвигаем ОБА конца на длину периода в bucket'ах: предыдущий отрезок
        // примыкает к текущему без нахлёста и без дыры. Считаем именно по краям, а не
        // по длине массива: у разрежённой оси (utm_trends) длина меньше числа bucket'ов.
        const n = bucketCount(baseFrom, baseTo, granularity)
        f = shiftBack(baseFrom, granularity, n)
        t = shiftBack(baseTo, granularity, n)
        shift = { amount: n, unit: granularity }
    }
    // shift отдаём наружу: по нему метки сравнительного периода переносятся на основную ось
    return { from: formatDateParam(f), to: formatDateParam(t), fromDate: f, toDate: t, shift }
}

/** Рекурсивный merge для overrides: объекты сливаются, массивы и скаляры заменяются. */
export function deepMerge(target, patch) {
    if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return patch
    const out = (target && typeof target === 'object' && !Array.isArray(target)) ? { ...target } : {}
    for (const key of Object.keys(patch)) {
        const val = patch[key]
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            out[key] = deepMerge(out[key], val)
        } else {
            out[key] = val
        }
    }
    return out
}

/**
 * Payload запроса сравнения: копия базового с подменёнными датами и наложенным
 * `overrides` (сюда же лягут будущие сегменты/когорты).
 *
 * @param {{labels: string[], sparse: boolean}|string[]} [axis] ось основного ответа — см. resolveComparePeriod
 * @returns {{payload: object, period: object}|null}
 */
export function buildComparePayload(basePayload, compare, axis = null) {
    if (!basePayload || !compare) return null
    const granularity = basePayload.source_request?.granularity || 'day'
    const params = basePayload.settings?.params || {}
    const period = resolveComparePeriod(params[':date_from'], params[':date_to'], compare, granularity, axis)
    if (!period) return null

    let payload = JSON.parse(JSON.stringify(basePayload))
    payload.settings = payload.settings || {}
    payload.settings.params = {
        ...(payload.settings.params || {}),
        ':date_from': period.from,
        ':date_to': period.to
    }
    if (compare.overrides) {
        payload = deepMerge(payload, compare.overrides)
    }
    return { payload, period }
}

/**
 * Изменение текущего значения относительно сравнительного.
 * pct = null, когда база нулевая (рост «с нуля» в процентах не выражается).
 */
export function computeDelta(current, previous) {
    const cur = Number(current) || 0
    const prev = Number(previous) || 0
    const abs = cur - prev
    const pct = prev === 0 ? null : (abs / Math.abs(prev)) * 100
    return { abs, pct }
}

/**
 * Сопоставляет строки SQL-ответа сравнительного периода строкам основного.
 *
 * У SQL-отчёта нет общей оси: строки — это просто результат пользовательского запроса.
 * Поэтому два правила, в порядке приоритета:
 *  1. Есть ключевые колонки (ось X, если она НЕ дата, плюс колонка разбивки) —
 *     сопоставляем по их значениям. Так `GROUP BY gender` сравнивается по полу.
 *  2. Иначе (ось X — дата, либо ключей нет) — по порядковому номеру строки: даты
 *     двух периодов заведомо не совпадают, а ORDER BY у обоих запросов один и тот же.
 *
 * @returns {Array} массив длиной с mainRows: строка сравнения либо null
 */
export function alignSqlCompareRows(mainRows, compareRows, sourceRequest, columns = []) {
    const main = Array.isArray(mainRows) ? mainRows : []
    const cmp = Array.isArray(compareRows) ? compareRows : []
    if (!main.length || !cmp.length) return main.map(() => null)

    const isDateColumn = (name) => columns.some(c => c.name === name && c.type === 'date')

    const keyColumns = []
    const x = sourceRequest?.x
    if (x && !isDateColumn(x)) keyColumns.push(x)
    for (const sep of (sourceRequest?.separators || [])) {
        if (sep && sep !== x) keyColumns.push(sep)
    }

    if (!keyColumns.length) {
        return main.map((_, i) => cmp[i] ?? null)
    }

    const rowKey = (row) => keyColumns.map(c => String(row?.[c] ?? '')).join('\x00')
    const index = new Map()
    for (const row of cmp) {
        const key = rowKey(row)
        if (!index.has(key)) index.set(key, row)
    }
    return main.map(row => index.get(rowKey(row)) ?? null)
}

/** '+12,4 %' / '−3,1 %' / null */
export function formatDeltaPct(current, previous, locale) {
    const { pct } = computeDelta(current, previous)
    if (pct === null || !isFinite(pct)) return null
    const sign = pct > 0 ? '+' : (pct < 0 ? '−' : '')
    const abs = Math.abs(pct)
    const digits = abs < 10 ? 1 : 0
    return sign + abs.toLocaleString(locale || undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
    }) + '%'
}
